import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, InputNumber, Select, Switch, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
import NodeIdEditor, { BLANK_NUMERIC_NODE_ID } from "./NodeIdEditor";
// FLATBUFFER ENUMS
import { HistoryReadKind } from "../../../../../flatbuffers/generated/opc-ua/history-read-kind";
// INTERFACES
import type {
  OpcUaHistoryReadForm,
  OpcUaNodeIdForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Panel } = Collapse;
const { Option } = Select;

const HISTORY_READ_KIND_OPTIONS: { value: HistoryReadKind; label: string }[] = [
  { value: HistoryReadKind.Raw, label: "Raw" },
  { value: HistoryReadKind.Modified, label: "Modified" },
  { value: HistoryReadKind.Processed, label: "Processed" },
  { value: HistoryReadKind.AtTime, label: "AtTime" },
  { value: HistoryReadKind.Events, label: "Events" },
];

function isNodeComplete(node: OpcUaNodeIdForm): boolean {
  if (node.identifierKind === "browsepath") return (node.hops ?? []).length > 0;
  return node.identifier.trim() !== "";
}

function describeNode(node: OpcUaNodeIdForm): string {
  if (node.identifierKind === "browsepath") {
    return `browsepath(${(node.hops ?? []).map((h) => h.targetName).join("/")})`;
  }
  return `ns=${node.namespaceIndex};${node.identifierKind}=${node.identifier}`;
}

/** Historical read configurations (OPC UA Part 11): which node, over what time range, at what resolution. */
export default function HistoryReadsEditor({
  historyReads,
  onChangeHistoryReads,
}: {
  historyReads: OpcUaHistoryReadForm[];
  onChangeHistoryReads: (rows: OpcUaHistoryReadForm[]) => void;
}) {
  const [t] = useTranslation("global");
  const [draft, setDraft] = useState<OpcUaHistoryReadForm>({
    node: { ...BLANK_NUMERIC_NODE_ID },
    kind: HistoryReadKind.Raw,
    startTime: "",
    endTime: "",
    numValuesPerNode: 0,
    returnBounds: false,
    aggregateTypeNode: null,
    processingIntervalMs: 0,
  });

  const canAdd = isNodeComplete(draft.node);
  const onAdd = () => {
    if (!canAdd) return;
    onChangeHistoryReads([...historyReads, draft]);
    setDraft({
      node: { ...BLANK_NUMERIC_NODE_ID },
      kind: HistoryReadKind.Raw,
      startTime: "",
      endTime: "",
      numValuesPerNode: 0,
      returnBounds: false,
      aggregateTypeNode: null,
      processingIntervalMs: 0,
    });
  };
  const onRemove = (index: number) =>
    onChangeHistoryReads(historyReads.filter((_, i) => i !== index));

  return (
    <div className="container mt-3">
      <Collapse>
        <Panel
          header={t("general.opcUaHistoryReads", { total: historyReads.length })}
          key="history-reads"
        >
          {historyReads.map((read, index) => (
            <div
              key={`${read.node.namespaceIndex}-${read.node.identifier}-${index}`}
              className="mb-3 generalStyles__flex"
              style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
            >
              <div style={{ width: "100%" }}>
                <div className="generalStyles__textEllipsis">
                  {describeNode(read.node)} —{" "}
                  {HISTORY_READ_KIND_OPTIONS.find((o) => o.value === read.kind)?.label} [
                  {read.startTime || "…"} → {read.endTime || "…"}]
                </div>
              </div>
              <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                <Tooltip title="Delete">
                  <Button
                    onClick={() => onRemove(index)}
                    className="buttonStyle__29"
                    shape="circle"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                </Tooltip>
              </div>
            </div>
          ))}
          <NodeIdEditor node={draft.node} onChangeNode={(node) => setDraft({ ...draft, node })} />
          <div className="row mt-2">
            <div className="col-12 col-md-3">
              <span className="generalStyles__inputLabel">Read Kind</span>
              <Select
                className="generalStyles__width100"
                size="large"
                value={draft.kind}
                onChange={(kind: HistoryReadKind) => setDraft({ ...draft, kind })}
              >
                {HISTORY_READ_KIND_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="col-12 col-md-4">
              <FormInputText
                inputValue={draft.startTime}
                inputLabelName="Start Time (ISO 8601)"
                inputOnChange={(value) => setDraft({ ...draft, startTime: value })}
                inputVariableName="startTime"
                isRequired={false}
                disabledInput={false}
              />
            </div>
            <div className="col-12 col-md-4">
              <FormInputText
                inputValue={draft.endTime}
                inputLabelName="End Time (ISO 8601)"
                inputOnChange={(value) => setDraft({ ...draft, endTime: value })}
                inputVariableName="endTime"
                isRequired={false}
                disabledInput={false}
              />
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-12 col-md-3">
              <span className="generalStyles__inputLabel">Num Values Per Node</span>
              <InputNumber
                className="generalStyles__width100"
                size="large"
                min={0}
                value={draft.numValuesPerNode}
                onChange={(v) => setDraft({ ...draft, numValuesPerNode: v ?? 0 })}
              />
            </div>
            <div className="col-12 col-md-3">
              <div className="generalStyles__flex">
                <span className="generalStyles__inputLabel">Return Bounds</span>
                <Switch
                  checked={draft.returnBounds}
                  onChange={(checked) => setDraft({ ...draft, returnBounds: checked })}
                  style={{ marginLeft: 8 }}
                />
              </div>
            </div>
            <div className="col-12 col-md-3">
              <span className="generalStyles__inputLabel">
                Processing Interval (ms, Processed kind)
              </span>
              <InputNumber
                className="generalStyles__width100"
                size="large"
                min={0}
                value={draft.processingIntervalMs}
                onChange={(v) => setDraft({ ...draft, processingIntervalMs: v ?? 0 })}
              />
            </div>
            <div className="col-12 col-md-3">
              <Button
                onClick={onAdd}
                disabled={!canAdd}
                className={`generalStyles__width100 ${canAdd ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
}
